import { defineStore } from "pinia"
import type { Article, ArticleDetail } from "../../types"
import { reportClientApiDuration, reportClientApiError, reportBusinessSuccess, reportBusinessError } from "../utils/metrics"

type ArticleQueryValue = string | string[] | undefined

type FetchStatus = "idle" | "loading" | "success" | "error"

function buildQueryString(params: Record<string, ArticleQueryValue>): string {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          searchParams.append(key, item)
        }
      })
      return
    }

    searchParams.append(key, value)
  })

  return searchParams.toString()
}

function getApiBaseUrl(): string {
  const configuredBase = import.meta.env.VITE_API_BASE_URL || "/api"
  const normalizedBase = configuredBase.startsWith("/") ? configuredBase : `/${configuredBase}`

  if (!import.meta.env.SSR) {
    return normalizedBase
  }

  const port = process.env.PORT || "9000"
  return `http://127.0.0.1:${port}${normalizedBase}`
}

function buildApiUrl(path: string, params?: Record<string, ArticleQueryValue>): string {
  const base = getApiBaseUrl().replace(/\/$/, "")
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  const url = `${base}${normalizedPath}`

  if (!params) {
    return url
  }

  const query = buildQueryString(params)
  return query ? `${url}?${query}` : url
}

function resolveErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallbackMessage
}

export const useArticleStore = defineStore("article", {
  state: () => ({
    articles: [] as Article[],
    articlePage: null as ArticleDetail | null,
    hasMore: false,
    nextPageUrl: "",
    fetchStatus: "idle" as FetchStatus,
    errorMessage: "",
    // 用于通知首页刷新的标志
    refreshHome: false as boolean
  }),

  actions: {
    async fetchArticles(params: { next?: string; category?: string | string[]; author?: string | string[]; title?: string | string[]; sort?: string }) {
      const start = Date.now()
      // 校验 next 参数，防止无效游标导致服务端报错
      if (params.next) {
        const isScoreMode = params.sort === 'score'
        const parsed = isScoreMode ? parseFloat(params.next) : parseInt(params.next)
        if (isNaN(parsed) || !isFinite(parsed)) {
          console.warn('[Store] Invalid next cursor, ignoring:', params.next)
          delete params.next
        }
      }
      this.fetchStatus = "loading"
      this.errorMessage = ""

      try {
        const response = await fetch(buildApiUrl("/articles", params))
        if (!response.ok) {
          console.error("[Store] fetchArticles 响应失败:", response.status)
          throw new Error("网络异常，请重试")
        }

        const { code, data } = await response.json()
        if (!code) {
          const { list, next } = data
          this.articles = list
          this.hasMore = !!next
          // 构建包含所有查询参数的分页 URL
          if (next) {
            const nextPageParams: Record<string, string | string[]> = { ...params }
            nextPageParams.next = next
            const queryString = buildQueryString(nextPageParams)
            this.nextPageUrl = `/?${queryString}`
          } else {
            this.nextPageUrl = ""
          }
          this.fetchStatus = "success"
          reportClientApiDuration("fetch_articles", Date.now() - start)
          reportBusinessSuccess("article_load")
          return { list, next }
        }

        throw new Error("获取文章列表失败，请稍后重试")
      } catch (error: any) {
        console.error("[Store] fetchArticles 异常:", error)
        this.fetchStatus = "error"
        this.errorMessage = resolveErrorMessage(error, "网络异常，请重试")
        reportClientApiError("fetch_articles", error?.name || "Unknown")
        reportBusinessError("article_load")
        throw error
      }
    },

    async fetchArticle(title: string) {
      const start = Date.now()
      this.fetchStatus = "loading"
      this.errorMessage = ""

      try {
        const url = buildApiUrl(`/articles/${title}`)
        const response = await fetch(url)
        if (!response.ok) {
          console.error("[Store] 响应失败:", response.status)
          throw new Error("网络异常，请重试")
        }

        const { code, data } = await response.json()
        if (!code) {
          this.articlePage = data
          this.fetchStatus = "success"
          reportClientApiDuration("fetch_article", Date.now() - start)
          reportBusinessSuccess("article_detail")
          return data
        }

        throw new Error("获取文章详情失败，请稍后重试")
      } catch (error: any) {
        console.error("[Store] fetchArticle 异常:", error)
        this.fetchStatus = "error"
        this.errorMessage = resolveErrorMessage(error, "网络异常，请重试")
        reportClientApiError("fetch_article", error?.name || "Unknown")
        reportBusinessError("article_detail")
        throw error
      }
    },

    // 标记需要刷新首页（从文章页返回首页时使用）
    markHomeRefresh() {
      this.refreshHome = true
    },

    // 清除刷新标志
    clearHomeRefresh() {
      this.refreshHome = false
    }
  }
})
